import { monsterPhotos, MONSTER_SHEET_SIZE, type PhotoRect } from '../data/guide/monsterPhotoSheets'
import { extraMonsterPhotos, EXTRA_SHEET_SIZE } from '../data/guide/monsterExtraPhotoSheets'
import type { IndexRecord } from '../data/guide/monsterPhotoIndexNotes'
import type { WorldData } from '../types/world'

/*
 * Contraste del índice original contra las láminas ya cargadas.
 * "Contrastado" SOLO cuando coinciden ID, paquete/lámina y recorte (solapamiento ≥ 85 %, escalado al tamaño de la lámina).
 */

export type ContrastStatus = 'coincide' | 'recorte distinto' | 'otra lámina' | 'no está en nuestras láminas' | 'índice sin lámina o recorte'

export type ContrastRow = {
  id: number
  catalogName?: string
  status: ContrastStatus
  detail: string
  overlap?: number
  record: IndexRecord
}

type Ours = { pack: 'inicial' | 'extra'; sheet: number; rect: PhotoRect; size: number }

const ours = new Map<number, Ours[]>()
monsterPhotos.forEach((p) => ours.set(p.id, [...(ours.get(p.id) ?? []), { pack: 'inicial', sheet: p.sheet, rect: p.rect, size: MONSTER_SHEET_SIZE }]))
extraMonsterPhotos.forEach((p) => ours.set(p.id, [...(ours.get(p.id) ?? []), { pack: 'extra', sheet: p.sheet, rect: p.rect, size: EXTRA_SHEET_SIZE }]))

const MATCH = 0.85

function iou(a: PhotoRect, b: PhotoRect): number {
  const x1 = Math.max(a[0], b[0])
  const y1 = Math.max(a[1], b[1])
  const x2 = Math.min(a[0] + a[2], b[0] + b[2])
  const y2 = Math.min(a[1] + a[3], b[1] + b[3])
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1)
  const union = a[2] * a[3] + b[2] * b[3] - inter
  return union > 0 ? inter / union : 0
}

const fmt = (r: PhotoRect) => `[${r.map((n) => Math.round(n)).join(', ')}]`

export function contrastPhotoIndex(w: WorldData, records: IndexRecord[]) {
  const rows: ContrastRow[] = records.map((r) => {
    const catalogName = w.monsters.get(r.id)?.name
    const mine = ours.get(r.id) ?? []
    if (!mine.length) return { id: r.id, catalogName, status: 'no está en nuestras láminas', detail: `índice: ${r.sheetRaw || '—'}`, record: r }
    if (!r.rect || r.sheetNumber === undefined) return { id: r.id, catalogName, status: 'índice sin lámina o recorte', detail: `sheet «${r.sheetRaw}»`, record: r }
    const sameSheet = mine.filter((o) => o.sheet === r.sheetNumber && (!r.sheetPack || o.pack === r.sheetPack))
    if (!sameSheet.length) {
      return { id: r.id, catalogName, status: 'otra lámina', detail: `índice: ${r.sheetRaw} · guía: ${mine.map((o) => `${o.pack === 'extra' ? 'EXTRA' : 'LIMPIOS'} ${o.sheet}`).join(', ')}`, record: r }
    }
    let best = { o: sameSheet[0], v: 0, scaled: r.rect as PhotoRect }
    sameSheet.forEach((o) => {
      const k = r.sheetWidth ? o.size / r.sheetWidth : 1
      const scaled = r.rect!.map((n) => n * k) as PhotoRect
      const v = iou(scaled, o.rect)
      if (v > best.v) best = { o, v, scaled }
    })
    const pct = Math.round(best.v * 100)
    return {
      id: r.id,
      catalogName,
      status: best.v >= MATCH ? 'coincide' : 'recorte distinto',
      detail: `${best.o.pack === 'extra' ? 'EXTRA' : 'LIMPIOS'} ${best.o.sheet} · índice ${fmt(best.scaled)} · guía ${fmt(best.o.rect)} · solapamiento ${pct} %`,
      overlap: best.v,
      record: r,
    }
  })

  const indexIds = new Set(records.map((r) => r.id))
  const missingFromIndex = Array.from(ours.keys()).filter((id) => !indexIds.has(id)).sort((a, b) => a - b)
  const by = (s: ContrastStatus) => rows.filter((r) => r.status === s)
  return {
    rows,
    total: records.length,
    matched: by('coincide'),
    discrepancies: rows.filter((r) => r.status !== 'coincide'),
    notInCatalog: rows.filter((r) => !r.catalogName),
    family: rows.filter((r) => r.record.familyReference),
    indexVisuallyChecked: rows.filter((r) => r.record.indexVisuallyChecked).length,
    retouched: rows.filter((r) => r.record.retouched).length,
    withSourcePage: rows.filter((r) => r.record.sourcePage).length,
    missingFromIndex,
  }
}

export type PhotoIndexContrast = ReturnType<typeof contrastPhotoIndex>

export function formatContrast(c: PhotoIndexContrast): string {
  return [
    'CONTRASTE DEL ÍNDICE ORIGINAL (VANGUARD_MONSTRUOS_INDICE_COMPACTO.json)',
    `IDs en el índice: ${c.total} (referencia 285)`,
    `Contrastados (ID + lámina + recorte ≥ 85 %): ${c.matched.length}`,
    `Referencias de familia (family_reference=true): ${c.family.length} (referencia 101)`,
    `visually_checked en el índice (antecedente, no inspección actual): ${c.indexVisuallyChecked}`,
    `retouched=true: ${c.retouched} · con source_page: ${c.withSourcePage}`,
    `IDs del índice que no están en 06_monsters: ${c.notInCatalog.map((r) => r.id).join(', ') || 'ninguno'}`,
    `IDs de nuestras láminas que no están en el índice: ${c.missingFromIndex.join(', ') || 'ninguno'}`,
    '',
    `DISCREPANCIAS (${c.discrepancies.length})`,
    ...c.discrepancies.map((r) => `${r.id}\t${r.catalogName ?? 'no está en 06_monsters'}\t${r.status}\t${r.detail}`),
  ].join('\n')
}
